import type { ExtractedEvents } from '../av/events';
import { secondsToTicks, TICKS_PER_BEAT } from '../av/time';
import type { AvSession, EventCategory, Project, VisualizerLayer } from '../domain/schemas';
import { BAND_COUNT, type AudioFeatures } from './analysis';
import { bandSeries, eventLevelAt, eventTrack, hash01, lastAtOrBefore, levelAt, spectrumAt, waveAt } from './features';

/**
 * Visualizer evaluation (Phase 4C): a pure function of (layers, events, tempo, features,
 * session position) → plain data per layer. The renderer draws it; tests compare it.
 * Nothing accumulates between calls: particles, pulse rings and grid scrolling are
 * computed from the triggers before t, so seeking anywhere is exact and preview and
 * export agree.
 *
 * `mode` per layer is what actually drives it: "measured" (analysed audio) or "events"
 * (musical events, synthetic). A measured layer without analysis falls back to "events"
 * and is reported in `fallback` — never presented as measured.
 */

export interface VisualizerInput {
  layers: readonly VisualizerLayer[];
  events: ExtractedEvents;
  bpm: number;
  features: AudioFeatures | null;
}

interface LayerBase<T extends VisualizerLayer['type']> {
  type: T;
  layer: Extract<VisualizerLayer, { type: T }>;
  mode: 'measured' | 'events';
}

export type LayerFrame =
  | (LayerBase<'bars'> & { values: Float32Array })
  | (LayerBase<'radial'> & { values: Float32Array; angle: number })
  | (LayerBase<'waveform'> & { points: Float32Array })
  | (LayerBase<'pulse'> & { rings: { progress: number; strength: number }[]; core: number; flash: number })
  | (LayerBase<'particles'> & { particles: Float32Array; count: number })
  | (LayerBase<'grid'> & { offset: number; glow: number });

export interface VisualizerFrame {
  layers: LayerFrame[];
  /** Screen flash from pulse layers (alpha bounded by the schema, ≤ 0.6). */
  flash: { color: string; alpha: number } | null;
  /** Ids of measured layers drawn from events because no analysis is available. */
  fallback: string[];
}

const LOW: ReadonlySet<EventCategory> = new Set(['kick', 'bass808']);
const MID: ReadonlySet<EventCategory> = new Set(['snare', 'clap', 'perc', 'cowbell', 'lead', 'pad']);
const HIGH: ReadonlySet<EventCategory> = new Set(['hats']);

const beatsAt = (t: number, bpm: number) => secondsToTicks(t, bpm) / TICKS_PER_BEAT;

/** Group levels (low/mid/high/other) of the layer's categories at t (event-driven). */
function eventGroups(input: VisualizerInput, layer: VisualizerLayer, t: number) {
  const cats = layer.response.categories;
  const pick = (set: ReadonlySet<EventCategory> | null) => {
    const list = cats.filter((c) => (set ? set.has(c) : !LOW.has(c) && !MID.has(c) && !HIGH.has(c)));
    return list.length ? eventLevelAt(eventTrack(input.events, list), t, layer.response.decay) : 0;
  };
  return { low: pick(LOW), mid: pick(MID), high: pick(HIGH), other: pick(null) };
}

/**
 * Synthetic (event-driven) spectrum of `n` bars: low bars follow kick/808, the middle
 * snares/claps/percussion, the top hats; other categories lift everything. A seeded
 * variation per bar and sixteenth keeps it lively. Labelled "Event-driven", never
 * presented as frequency analysis.
 */
function syntheticSpectrum(input: VisualizerInput, layer: VisualizerLayer, t: number, n: number): Float32Array {
  const g = eventGroups(input, layer, t);
  const step = Math.floor(beatsAt(t, input.bpm) * 4);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const p = n > 1 ? i / (n - 1) : 0;
    const wl = Math.max(0, 1 - p * 3);
    const wm = Math.max(0, 1 - Math.abs(p - 0.5) * 3);
    const wh = Math.max(0, p * 3 - 2);
    const base = g.low * wl + g.mid * wm + g.high * wh + g.other * 0.5;
    const shape = 1 - 0.35 * p;
    const variation = 0.7 + 0.3 * hash01(layer.seed, i, step);
    out[i] = Math.min(1.5, base * shape * variation * layer.response.sensitivity);
  }
  return out;
}

/** Measured spectrum resampled to `n` bars (log-spaced bands, linear interpolation). */
function measuredSpectrum(f: AudioFeatures, layer: VisualizerLayer, t: number, n: number): Float32Array {
  const bands = spectrumAt(f, t, layer.response.smoothing, layer.response.decay);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const b = n > 1 ? (i / (n - 1)) * (BAND_COUNT - 1) : 0;
    const lo = Math.floor(b);
    const hi = Math.min(BAND_COUNT - 1, lo + 1);
    const v = bands[lo]! + (bands[hi]! - bands[lo]!) * (b - lo);
    out[i] = Math.min(1.5, v * layer.response.sensitivity);
  }
  return out;
}

/** Scalar level of a layer at t: measured band, or the envelope of its events. */
function scalarLevel(input: VisualizerInput, layer: VisualizerLayer, mode: 'measured' | 'events', t: number): number {
  const r = layer.response;
  const v = mode === 'measured' && input.features ? levelAt(input.features, bandSeries(r.band), t, r.smoothing, r.decay) : eventLevelAt(eventTrack(input.events, r.categories), t, r.decay);
  return Math.min(2, v * r.sensitivity);
}

/** Symmetry: bar i of n repeats the first n/k bars, mirrored on every other segment. */
function applySymmetry(values: Float32Array, k: number): Float32Array {
  if (k <= 1) return values;
  const n = values.length;
  const seg = Math.max(1, Math.floor(n / k));
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const s = Math.floor(i / seg);
    const j = i % seg;
    out[i] = values[s % 2 === 0 ? j : seg - 1 - j] ?? 0;
  }
  return out;
}

function evaluateLayer(input: VisualizerInput, layer: VisualizerLayer, t: number, fallback: string[]): LayerFrame {
  const wantsMeasured = layer.response.source === 'measured';
  const mode: 'measured' | 'events' = wantsMeasured && input.features ? 'measured' : 'events';
  if (wantsMeasured && !input.features) fallback.push(layer.id);
  switch (layer.type) {
    case 'bars': {
      const n = layer.params.count;
      return { type: 'bars', layer, mode, values: mode === 'measured' ? measuredSpectrum(input.features!, layer, t, n) : syntheticSpectrum(input, layer, t, n) };
    }
    case 'radial': {
      const n = layer.params.count;
      const seg = Math.max(1, Math.floor(n / layer.params.symmetry));
      const raw = mode === 'measured' ? measuredSpectrum(input.features!, layer, t, seg) : syntheticSpectrum(input, layer, t, seg);
      const values = new Float32Array(n);
      values.set(raw.subarray(0, Math.min(seg, n)));
      // Tempo-synced spin: `spin` turns per bar of music.
      const angle = 2 * Math.PI * layer.params.spin * (beatsAt(t, input.bpm) / 4);
      return { type: 'radial', layer, mode, values: applySymmetry(values, layer.params.symmetry), angle };
    }
    case 'waveform': {
      const n = layer.params.points;
      const win = layer.params.windowMs / 1000;
      const points = new Float32Array(n);
      if (mode === 'measured') {
        const f = input.features!;
        const gain = layer.response.sensitivity;
        for (let i = 0; i < n; i++) points[i] = Math.max(-1.5, Math.min(1.5, waveAt(f, t + (i / (n - 1) - 0.5) * win) * gain));
      } else {
        // Synthetic wave shaped by the event envelopes (labelled event-driven).
        const g = eventGroups(input, layer, t);
        const amp = Math.min(1.5, (g.low * 0.9 + g.mid * 0.6 + g.high * 0.3 + g.other * 0.4) * layer.response.sensitivity);
        const phase = beatsAt(t, input.bpm) * 2 * Math.PI;
        const cycles = 2 + 3 * g.high + 1.5 * g.mid;
        for (let i = 0; i < n; i++) {
          const x = i / (n - 1);
          const env = Math.sin(Math.PI * x);
          points[i] = amp * env * (0.7 * Math.sin(2 * Math.PI * cycles * x + phase) + 0.3 * Math.sin(2 * Math.PI * (cycles * 2.7) * x - phase * 1.3));
        }
      }
      return { type: 'waveform', layer, mode, points };
    }
    case 'pulse': {
      const track = eventTrack(input.events, layer.response.categories);
      const life = 0.2 + layer.response.decay * 1.5;
      const rings: { progress: number; strength: number }[] = [];
      let flash = 0;
      for (let i = lastAtOrBefore(track.times, t); i >= 0 && rings.length < layer.params.maxRings; i--) {
        const age = t - track.times[i]!;
        if (age >= life) break;
        const strength = mode === 'measured' ? Math.min(1, levelAt(input.features!, bandSeries(layer.response.band), track.times[i]!, 0, 0) * layer.response.sensitivity) : Math.min(1, (0.4 + 0.6 * track.velocities[i]!) * layer.response.sensitivity);
        rings.push({ progress: age / life, strength });
        if (layer.params.flash > 0 && age < 0.15) flash = Math.max(flash, layer.params.flash * strength * (1 - age / 0.15));
      }
      const core = 1 + scalarLevel(input, layer, mode, t) * 0.5;
      return { type: 'pulse', layer, mode, rings, core, flash };
    }
    case 'particles':
      return particles(input, layer, mode, t);
    case 'grid': {
      const offset = (((beatsAt(t, input.bpm) * layer.params.speed) % 1) + 1) % 1;
      const glow = layer.params.pulse * Math.min(1, scalarLevel(input, layer, mode, t));
      return { type: 'grid', layer, mode, offset, glow };
    }
  }
}

/**
 * Stateless particles: each trigger (a matching event, or a tick of the continuous rate)
 * emits particles whose parameters come from hash(seed, trigger, index); a particle's
 * state at t is a closed-form function of its age. The newest particles are kept up to
 * `count`. Output: x, y, size, alpha per particle (layer-local coordinates).
 */
function particles(input: VisualizerInput, layer: Extract<VisualizerLayer, { type: 'particles' }>, mode: 'measured' | 'events', t: number): LayerFrame {
  const p = layer.params;
  const out = new Float32Array(p.count * 4);
  let n = 0;
  const emit = (key: number, te: number, perTrigger: number) => {
    const age = t - te;
    for (let j = 0; j < perTrigger && n < p.count; j++) {
      const life = p.lifetime * (0.5 + 0.5 * hash01(layer.seed, key, j, 3));
      if (age < 0 || age >= life) continue;
      const ang = ((p.direction + (hash01(layer.seed, key, j, 1) - 0.5) * p.spread) * Math.PI) / 180;
      const speed = p.speed * (0.6 + 0.8 * hash01(layer.seed, key, j, 2));
      const ox = (hash01(layer.seed, key, j, 5) - 0.5) * layer.width;
      const oy = (hash01(layer.seed, key, j, 6) - 0.5) * layer.height;
      out[n * 4] = ox + Math.cos(ang) * speed * age;
      out[n * 4 + 1] = oy + Math.sin(ang) * speed * age + 0.5 * p.gravity * age * age;
      out[n * 4 + 2] = p.size * (0.5 + hash01(layer.seed, key, j, 4));
      out[n * 4 + 3] = 1 - age / life;
      n++;
    }
  };
  // Event bursts, newest first.
  if (p.perEvent > 0) {
    const track = eventTrack(input.events, layer.response.categories);
    for (let i = lastAtOrBefore(track.times, t); i >= 0 && n < p.count; i--) {
      const te = track.times[i]!;
      if (t - te >= p.lifetime) break;
      const strength = mode === 'measured' ? Math.min(1, levelAt(input.features!, bandSeries(layer.response.band), te, 0, 0) * layer.response.sensitivity) : Math.min(1, (0.5 + 0.5 * track.velocities[i]!) * layer.response.sensitivity);
      emit(track.ordinals[i]!, te, Math.round(p.perEvent * strength));
    }
  }
  // Continuous emission (gated by the measured level when measured), newest first.
  if (p.rate > 0) {
    const last = Math.floor(t * p.rate);
    const first = Math.ceil((t - p.lifetime) * p.rate);
    for (let k = last; k >= Math.max(0, first) && n < p.count; k--) {
      const te = k / p.rate;
      if (mode === 'measured') {
        const level = Math.min(1, levelAt(input.features!, bandSeries(layer.response.band), te, layer.response.smoothing, 0) * layer.response.sensitivity);
        if (hash01(layer.seed, k, 0, 9) >= level) continue;
      }
      emit(1_000_000 + k, te, 1);
    }
  }
  return { type: 'particles', layer, mode, particles: out, count: n };
}

/** Evaluates every visible layer at session position `t`. */
export function evaluateVisualizer(input: VisualizerInput, t: number): VisualizerFrame {
  const fallback: string[] = [];
  const layers: LayerFrame[] = [];
  let flash: VisualizerFrame['flash'] = null;
  for (const layer of input.layers) {
    if (!layer.visible) continue;
    const f = evaluateLayer(input, layer, t, fallback);
    layers.push(f);
    if (f.type === 'pulse' && f.flash > 0 && (!flash || f.flash > flash.alpha)) flash = { color: layer.style.primary, alpha: f.flash };
  }
  return { layers, flash, fallback };
}

/** The visualizer of a session at a position (null without layers): the one function preview and export call. */
export function sessionVisualizer(project: Project, session: AvSession, events: ExtractedEvents, features: AudioFeatures | null, t: number): VisualizerFrame | null {
  if (session.visualizer.layers.length === 0) return null;
  return evaluateVisualizer({ layers: session.visualizer.layers, events, bpm: project.settings.bpm, features }, t);
}

/** True when any visible layer asks for measured audio (export obtains the analysis then). */
export const needsAnalysis = (session: AvSession): boolean => session.visualizer.layers.some((l) => l.visible && l.response.source === 'measured');
